// ===================================================================
// KAIXO - useLatidoDorado hook
// ===================================================================
// Para un post específico, devuelve su progreso hacia Latido Dorado,
// los slots disponibles si está desbloqueado, y la función claim().
// ===================================================================

import { useState, useEffect, useCallback, useRef } from 'react';
import api from '../services/api';

// Flag global: si el endpoint responde 404 una vez (sistema
// deshabilitado en este backend), no volvemos a pedirlo en la sesión.
// Evita que la consola se llene de 404s por cada post del feed.
let __ldSystemDisabled = false;

export function useLatidoDorado(postId) {
    const [progress, setProgress] = useState(null);
    const [loading, setLoading] = useState(true);
    const [claiming, setClaiming] = useState(false);
    const [lastClaim, setLastClaim] = useState(null);
    const [error, setError] = useState(null);
    // Flag "mounted": evita setState tras unmount cuando la red es lenta
    // y el componente padre (ej. FeedItem) se desmonta mientras esperamos
    // la respuesta de /progress o /claim.
    const mountedRef = useRef(true);
    useEffect(() => () => { mountedRef.current = false; }, []);

    const refresh = useCallback(async () => {
        if (!postId) return;
        if (__ldSystemDisabled) {
            if (mountedRef.current) setLoading(false);
            return;
        }
        try {
            const { data } = await api.get(`/latidoDorado/post/${postId}/progress`);
            if (!mountedRef.current) return;
            setProgress(data.progress);
        } catch (e) {
            if (e?.response?.status === 404) {
                __ldSystemDisabled = true;
            }
            if (mountedRef.current) setProgress(null);
        } finally {
            if (mountedRef.current) setLoading(false);
        }
    }, [postId]);

    useEffect(() => { refresh(); }, [refresh]);

    // Escucha desbloqueos y cambios de likes para refrescar el progreso
    useEffect(() => {
        const handler = (e) => {
            if (!e.detail) return;
            if (e.detail.postId === String(postId)) refresh();
        };
        window.addEventListener('latidoDorado:unlocked', handler);
        window.addEventListener('latidoDorado:likeChanged', handler);
        return () => {
            window.removeEventListener('latidoDorado:unlocked', handler);
            window.removeEventListener('latidoDorado:likeChanged', handler);
        };
    }, [postId, refresh]);

    const claim = useCallback(async () => {
        if (!postId) return null;
        setClaiming(true);
        setError(null);
        try {
            const { data } = await api.post(`/latidoDorado/post/${postId}/claim`);
            if (!mountedRef.current) return data;
            setLastClaim(data);
            refresh();
            return data;
        } catch (e) {
            if (!mountedRef.current) return null;
            const msg = e?.response?.data?.error || e.message;
            setError(msg);
            return null;
        } finally {
            if (mountedRef.current) setClaiming(false);
        }
    }, [postId, refresh]);

    return { progress, loading, claim, claiming, lastClaim, error, refresh };
}

export default useLatidoDorado;
